import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Printer, Plus, Trash2, Search, Layers, Eye } from 'lucide-react';
import { PRODUTOS_PALETE_INICIAL, ordenarProdutosPalete } from './produtoPaleteData';
import { corrigirGrafiaBerlin } from '../../utils/corrigirGrafiaBerlin';
import {
  agruparQtdPorProdutoCorDoLote,
  coletarCoresPaleteDeLotes,
  distribuirQtdPorVolumesProduto,
  normalizarCorPalete,
  obterVolumesAtivosProdutoPalete,
  resolverCorPaleteParaLote,
} from '../../utils/casarCorPaleteLotes';
import {
  coletarProdutosPaleteDeLotes,
  encontrarProdutoPalete,
  normalizarNomeProdutoPalete,
} from '../../utils/casarProdutoPaleteLotes';

interface LoteProdutoConfig {
  id: string;
  lote: string;
  produtoNome: string;
  qtd: number;
  corProduto?: string;
}

interface ProdutoPaleteConfig {
  /** Identificador estável — evita perder foco ao digitar o nome. */
  id: string;
  produto: string;
  vol1: number;
  vol2?: number;
  vol3?: number;
}

interface FilaItem {
  id: string;
  lote: string;
  quantidadePorCor: Record<string, number>;
}

const CORES_INICIAIS = [
  'BRANCO',
  'PRETO',
  'CEDRO',
  'OFF WHITE',
  'CEDRO/OFF',
  'OFF/CEDRO',
  'OFF/CEDRO/RIPADO',
  'RIPADO 3D',
  'OFF/NATURALE',
  'PRETO/CEDRO',
  'CEDRO/PRETO',
  'OUTROS',
];

const STORAGE_CORES = 'produto-palete-cores-ade';
const STORAGE_PRODUTOS_PALETE = 'produto-palete-config-ade';
const STORAGE_LOTES_IMPRESSOS = 'produto-palete-lotes-impressos-ade';
const STORAGE_ULTIMO_LOTE_PARADO = 'produto-palete-ultimo-lote-parado-ade';
const DB_PALETE_NAME = 'ade-produto-palete-db';
const DB_PALETE_VERSION = 1;
const STORE_PALETE_CONFIG = 'produtoPaleteConfig';
const STORE_PALETE_META = 'produtoPaleteMeta';
const KEY_PRODUTOS = 'produtos';
const KEY_CORES = 'cores';
const KEY_LOTES_IMPRESSOS = 'lotesImpressos';
const KEY_ULTIMO_LOTE = 'ultimoLoteParado';

function abrirDbPalete(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    try {
      const req = indexedDB.open(DB_PALETE_NAME, DB_PALETE_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_PALETE_CONFIG)) {
          db.createObjectStore(STORE_PALETE_CONFIG);
        }
        if (!db.objectStoreNames.contains(STORE_PALETE_META)) {
          db.createObjectStore(STORE_PALETE_META);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function lerDbPalete<T>(store: string, key: string): Promise<T | null> {
  return new Promise(async (resolve) => {
    const db = await abrirDbPalete();
    if (!db) {
      resolve(null);
      return;
    }
    try {
      const tx = db.transaction(store, 'readonly');
      const os = tx.objectStore(store);
      const req = os.get(key);
      req.onsuccess = () => resolve((req.result as T) ?? null);
      req.onerror = () => resolve(null);
      tx.oncomplete = () => db.close();
      tx.onabort = () => db.close();
    } catch {
      db.close();
      resolve(null);
    }
  });
}

function legadoPaleteTemDados(key: string): boolean {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
}

async function salvarArrayPaleteSeguro<T>(
  store: string,
  dbKey: string,
  legadoKey: string,
  items: T[],
  lerDb: () => Promise<T[] | null>
): Promise<boolean> {
  if (items.length === 0) {
    const existente = await lerDb();
    if (existente && existente.length > 0) return false;
    if (legadoPaleteTemDados(legadoKey)) return false;
  }
  try {
    localStorage.setItem(legadoKey, JSON.stringify(items));
  } catch {
    /* ignora quota */
  }
  return salvarDbPalete(store, dbKey, items);
}

function salvarDbPalete(store: string, key: string, value: unknown): Promise<boolean> {
  return new Promise(async (resolve) => {
    const db = await abrirDbPalete();
    if (!db) {
      resolve(false);
      return;
    }
    try {
      const tx = db.transaction(store, 'readwrite');
      tx.objectStore(store).put(value, key);
      tx.oncomplete = () => {
        db.close();
        resolve(true);
      };
      tx.onerror = () => {
        db.close();
        resolve(false);
      };
      tx.onabort = () => {
        db.close();
        resolve(false);
      };
    } catch {
      db.close();
      resolve(false);
    }
  });
}

function normalizar(s: string) {
  return (s || '').trim().toUpperCase();
}

function novoIdProdutoPalete() {
  return `palete-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function garantirIdsProdutosPalete(
  lista: Array<Partial<ProdutoPaleteConfig> & { produto: string; vol1: number }>
): ProdutoPaleteConfig[] {
  return lista.map((p) => ({
    id: p.id || novoIdProdutoPalete(),
    produto: corrigirGrafiaBerlin(String(p.produto ?? '')),
    vol1: Number(p.vol1) || 0,
    vol2: p.vol2 != null ? Number(p.vol2) : undefined,
    vol3: p.vol3 != null ? Number(p.vol3) : undefined,
  }));
}

function chaveQtd(produto: string, cor: string, vol: number) {
  return `${produto}::${cor}::v${vol}`;
}

interface ProdutoPorPaleteProps {
  lotesProdutos: LoteProdutoConfig[];
}

export default function ProdutoPorPalete({ lotesProdutos }: ProdutoPorPaleteProps) {
  const [produtosPalete, setProdutosPalete] = useState<ProdutoPaleteConfig[]>(() => {
    try {
      const s = localStorage.getItem(STORAGE_PRODUTOS_PALETE);
      if (!s) return garantirIdsProdutosPalete(PRODUTOS_PALETE_INICIAL);
      const parsed = JSON.parse(s);
      return Array.isArray(parsed) && parsed.length > 0
        ? ordenarProdutosPalete(garantirIdsProdutosPalete(parsed))
        : garantirIdsProdutosPalete(PRODUTOS_PALETE_INICIAL);
    } catch {
      return garantirIdsProdutosPalete(PRODUTOS_PALETE_INICIAL);
    }
  });

  const [buscaProdutoPalete, setBuscaProdutoPalete] = useState('');

  const [cores, setCores] = useState<string[]>(() => {
    try {
      const s = localStorage.getItem(STORAGE_CORES);
      if (!s) return CORES_INICIAIS;
      const parsed = JSON.parse(s);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : CORES_INICIAIS;
    } catch {
      return CORES_INICIAIS;
    }
  });

  const [novaCor, setNovaCor] = useState('');
  const [loteSelecionado, setLoteSelecionado] = useState('');
  const [quantidadePorCor, setQuantidadePorCor] = useState<Record<string, number>>({});
  const [fila, setFila] = useState<FilaItem[]>([]);
  const [mostrarPreview, setMostrarPreview] = useState(false);
  const [lotesImpressos, setLotesImpressos] = useState<string[]>(() => {
    try {
      const s = localStorage.getItem(STORAGE_LOTES_IMPRESSOS);
      if (!s) return [];
      const parsed = JSON.parse(s);
      return Array.isArray(parsed) ? parsed.map((x) => normalizar(String(x))).filter(Boolean) : [];
    } catch {
      return [];
    }
  });
  const [ultimoLoteParado, setUltimoLoteParado] = useState<string>(() => {
    try {
      return normalizar(localStorage.getItem(STORAGE_ULTIMO_LOTE_PARADO) || '');
    } catch {
      return '';
    }
  });
  const carregouPersistenciaRef = useRef(false);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      const [
        produtosDb,
        coresDb,
        lotesDb,
        ultimoDb,
      ] = await Promise.all([
        lerDbPalete<ProdutoPaleteConfig[]>(STORE_PALETE_CONFIG, KEY_PRODUTOS),
        lerDbPalete<string[]>(STORE_PALETE_CONFIG, KEY_CORES),
        lerDbPalete<string[]>(STORE_PALETE_META, KEY_LOTES_IMPRESSOS),
        lerDbPalete<string>(STORE_PALETE_META, KEY_ULTIMO_LOTE),
      ]);
      if (cancelado) return;

      const lerArrayLocalStorage = <T,>(key: string): T[] => {
        try {
          const raw = localStorage.getItem(key);
          if (!raw) return [];
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? (parsed as T[]) : [];
        } catch {
          return [];
        }
      };
      const lerStringLocalStorage = (key: string) => {
        try {
          return String(localStorage.getItem(key) || '');
        } catch {
          return '';
        }
      };

      const produtosLs = lerArrayLocalStorage<ProdutoPaleteConfig>(STORAGE_PRODUTOS_PALETE);
      const produtosEscolhidos =
        Array.isArray(produtosDb) && produtosDb.length > 0 && produtosDb.length >= produtosLs.length
          ? produtosDb
          : produtosLs;
      if (Array.isArray(produtosEscolhidos) && produtosEscolhidos.length > 0) {
        setProdutosPalete(ordenarProdutosPalete(garantirIdsProdutosPalete(produtosEscolhidos)));
      }

      const coresLs = lerArrayLocalStorage<string>(STORAGE_CORES);
      const coresEscolhidas =
        Array.isArray(coresDb) && coresDb.length > 0 && coresDb.length >= coresLs.length
          ? coresDb
          : coresLs;
      if (Array.isArray(coresEscolhidas) && coresEscolhidas.length > 0) {
        setCores(coresEscolhidas.map((c) => String(c).toUpperCase()));
      }

      const lotesLs = lerArrayLocalStorage<string>(STORAGE_LOTES_IMPRESSOS).map((x) =>
        normalizar(String(x))
      );
      const lotesEscolhidos =
        Array.isArray(lotesDb) && lotesDb.length > 0 && lotesDb.length >= lotesLs.length
          ? lotesDb
          : lotesLs;
      if (Array.isArray(lotesEscolhidos) && lotesEscolhidos.length > 0) {
        setLotesImpressos(
          Array.from(new Set(lotesEscolhidos.map((x) => normalizar(String(x))).filter(Boolean))).sort()
        );
        if ((!lotesDb || lotesDb.length === 0) && lotesLs.length > 0) {
          console.info(
            `[Sistema Ade] Produto por Palete: ${lotesLs.length} lote(s) impresso(s) recuperado(s) do backup localStorage.`
          );
        }
      }

      const ultimoLs = normalizar(lerStringLocalStorage(STORAGE_ULTIMO_LOTE_PARADO));
      const ultimoEscolhido = normalizar((ultimoDb || '').trim()) || ultimoLs;
      if (ultimoEscolhido) setUltimoLoteParado(ultimoEscolhido);

      carregouPersistenciaRef.current = true;
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    if (!carregouPersistenciaRef.current) return;
    void (async () => {
      await salvarArrayPaleteSeguro(
        STORE_PALETE_CONFIG,
        KEY_CORES,
        STORAGE_CORES,
        cores,
        () => lerDbPalete<string[]>(STORE_PALETE_CONFIG, KEY_CORES)
      );
    })();
  }, [cores]);

  useEffect(() => {
    if (!carregouPersistenciaRef.current) return;
    void (async () => {
      await salvarArrayPaleteSeguro(
        STORE_PALETE_CONFIG,
        KEY_PRODUTOS,
        STORAGE_PRODUTOS_PALETE,
        produtosPalete,
        () => lerDbPalete<ProdutoPaleteConfig[]>(STORE_PALETE_CONFIG, KEY_PRODUTOS)
      );
    })();
  }, [produtosPalete]);

  useEffect(() => {
    if (!carregouPersistenciaRef.current) return;
    void (async () => {
      await salvarArrayPaleteSeguro(
        STORE_PALETE_META,
        KEY_LOTES_IMPRESSOS,
        STORAGE_LOTES_IMPRESSOS,
        lotesImpressos,
        () => lerDbPalete<string[]>(STORE_PALETE_META, KEY_LOTES_IMPRESSOS)
      );
    })();
  }, [lotesImpressos]);

  useEffect(() => {
    if (!carregouPersistenciaRef.current) return;
    try {
      localStorage.setItem(STORAGE_ULTIMO_LOTE_PARADO, ultimoLoteParado);
    } catch {
      // Evita quebra do app quando a quota do storage estiver cheia.
    }
    void salvarDbPalete(STORE_PALETE_META, KEY_ULTIMO_LOTE, ultimoLoteParado);
  }, [ultimoLoteParado]);

  const lotesUnicos = useMemo(() => {
    const set = new Set<string>();
    lotesProdutos.forEach((lp) => set.add(normalizar(lp.lote)));
    return Array.from(set).sort();
  }, [lotesProdutos]);

  const produtoPaleteValido = useCallback(
    (nomeLote: string) => encontrarProdutoPalete(nomeLote, produtosPalete),
    [produtosPalete]
  );

  /** Inclui produtos dos lotes que ainda não existem na config da palete. */
  useEffect(() => {
    const novosNomes = coletarProdutosPaleteDeLotes(lotesProdutos, produtosPalete);
    if (novosNomes.length === 0) return;
    setProdutosPalete((prev) =>
      ordenarProdutosPalete([
        ...prev,
        ...novosNomes.map((nome) => ({
          id: novoIdProdutoPalete(),
          produto: nome,
          vol1: 28,
          vol2: undefined,
          vol3: undefined,
        })),
      ])
    );
  }, [lotesProdutos]);

  /** Inclui cores dos lotes na lista da palete (sem alterar lógica de impressão). */
  useEffect(() => {
    const novas = coletarCoresPaleteDeLotes(lotesProdutos, cores);
    if (novas.length === 0) return;
    setCores((prev) => {
      const chaves = new Set(prev.map((c) => normalizarCorPalete(c)));
      const merged = [...prev];
      for (const c of novas) {
        const ch = normalizarCorPalete(c);
        if (!chaves.has(ch)) {
          chaves.add(ch);
          merged.push(c);
        }
      }
      return merged.sort((a, b) => a.localeCompare(b, 'pt-BR'));
    });
  }, [lotesProdutos]);

  /** Ao selecionar lote, preenche qtd por cor e volume (V1/V2/V3) a partir dos dados de Lotes. */
  useEffect(() => {
    if (!loteSelecionado) return;

    const loteNorm = normalizar(loteSelecionado);
    const coresAtualizadas = [...cores];
    const novasCores = coletarCoresPaleteDeLotes(
      lotesProdutos.filter((lp) => normalizar(lp.lote) === loteNorm),
      coresAtualizadas
    );
    if (novasCores.length > 0) {
      const chaves = new Set(coresAtualizadas.map((c) => normalizarCorPalete(c)));
      for (const c of novasCores) {
        const ch = normalizarCorPalete(c);
        if (!chaves.has(ch)) {
          chaves.add(ch);
          coresAtualizadas.push(c);
        }
      }
      setCores(coresAtualizadas.sort((a, b) => a.localeCompare(b, 'pt-BR')));
    }

    const agrupados = agruparQtdPorProdutoCorDoLote(
      lotesProdutos,
      loteNorm,
      coresAtualizadas,
      (nomeLote) => produtoPaleteValido(nomeLote)
    );

    const novasQtd: Record<string, number> = {};
    for (const { produtoNorm, corPalete, qtd } of agrupados) {
      if (qtd <= 0) continue;
      const corFinal =
        resolverCorPaleteParaLote(corPalete, coresAtualizadas) || corPalete;
      const volumes = obterVolumesAtivosProdutoPalete(produtosPalete, produtoNorm);
      for (const { vol, qtd: qtdVol } of distribuirQtdPorVolumesProduto(qtd, volumes)) {
        novasQtd[chaveQtd(produtoNorm, corFinal, vol)] = qtdVol;
      }
    }

    setQuantidadePorCor(novasQtd);
  }, [loteSelecionado, lotesProdutos, produtoPaleteValido, produtosPalete]);

  const loteSelecionadoImpresso = useMemo(() => {
    if (!loteSelecionado) return false;
    return lotesImpressos.includes(normalizar(loteSelecionado));
  }, [loteSelecionado, lotesImpressos]);

  const produtosDoLote = useMemo(() => {
    if (!loteSelecionado) return [];
    const loteNorm = normalizar(loteSelecionado);
    const itens = lotesProdutos.filter((lp) => normalizar(lp.lote) === loteNorm);
    const porProduto = new Map<string, number>();
    itens.forEach((lp) => {
      const nome = (lp.produtoNome || '').trim();
      if (!nome) return;
      const match = encontrarProdutoPalete(nome, produtosPalete);
      if (match) {
        porProduto.set(match.chave, (porProduto.get(match.chave) ?? 0) + lp.qtd);
      }
    });
    return Array.from(porProduto.entries())
      .map(([nome, qtd]) => ({ nome, qtd }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base', numeric: true }));
  }, [lotesProdutos, loteSelecionado, produtosPalete]);

  const produtosPaleteVisiveis = useMemo(() => {
    const ordenados = ordenarProdutosPalete(produtosPalete);
    const termo = buscaProdutoPalete.trim().toUpperCase();
    if (!termo) return ordenados;
    return ordenados.filter((p) => normalizar(p.produto).includes(termo));
  }, [produtosPalete, buscaProdutoPalete]);

  const getConfigProduto = (nome: string) => {
    const match = encontrarProdutoPalete(nome, produtosPalete);
    if (!match) return undefined;
    return produtosPalete.find(
      (p) => normalizarNomeProdutoPalete(p.produto) === match.chave
    );
  };

  const getVolumes = (nome: string) => {
    const cfg = getConfigProduto(nome);
    if (!cfg) return [{ vol: 1, qtd: 28 }];
    const vols: { vol: number; qtd: number }[] = [{ vol: 1, qtd: cfg.vol1 }];
    if (cfg.vol2 != null) vols.push({ vol: 2, qtd: cfg.vol2 });
    if (cfg.vol3 != null) vols.push({ vol: 3, qtd: cfg.vol3 });
    return vols;
  };

  const ordenarProdutosPaleteAgora = () => {
    setProdutosPalete((prev) => ordenarProdutosPalete(prev));
  };

  const atualizarProdutoPalete = (
    id: string,
    campo: 'produto' | 'vol1' | 'vol2' | 'vol3',
    valor: string
  ) => {
    setProdutosPalete((prev) => {
      const next = prev.map((item) => {
        if (item.id !== id) return item;
        if (campo === 'produto') return { ...item, produto: valor.toUpperCase() };
        const numero = Number(valor);
        if (campo === 'vol1') return { ...item, vol1: Number.isFinite(numero) ? numero : 0 };
        if (!valor.trim()) return { ...item, [campo]: undefined };
        return { ...item, [campo]: Number.isFinite(numero) ? numero : undefined };
      });
      // Nome: não reordena a cada tecla (senão o input perde o foco).
      if (campo === 'produto') return next;
      return ordenarProdutosPalete(next);
    });
  };

  const adicionarProdutoPalete = () => {
    setProdutosPalete((prev) =>
      ordenarProdutosPalete([
        ...prev,
        { id: novoIdProdutoPalete(), produto: '', vol1: 0, vol2: undefined, vol3: undefined },
      ])
    );
  };

  const removerProdutoPalete = (id: string) => {
    setProdutosPalete((prev) => ordenarProdutosPalete(prev.filter((p) => p.id !== id)));
  };

  const adicionarCor = () => {
    const c = novaCor.trim().toUpperCase();
    if (c && !cores.includes(c)) {
      setCores((prev) => [...prev, c].sort());
      setNovaCor('');
    }
  };

  const atualizarQtdCor = (produtoNome: string, cor: string, vol: number, valor: number) => {
    const chave = chaveQtd(produtoNome, cor, vol);
    setQuantidadePorCor((prev) => ({ ...prev, [chave]: valor }));
  };

  const getQtdCor = (produtoNome: string, cor: string, vol: number) => {
    return Number(quantidadePorCor[chaveQtd(produtoNome, cor, vol)]) || 0;
  };

  const folhasDeFila = useMemo(() => {
    const folhas: Array<{ produto: string; cor: string; fracao: string; lote: string }> = [];
    fila.forEach((item) => {
      const loteNorm = normalizar(item.lote);
      const itens = lotesProdutos.filter((lp) => normalizar(lp.lote) === loteNorm);
      const porProduto = new Map<string, number>();
      itens.forEach((lp) => {
        const nome = (lp.produtoNome || '').trim();
        if (!nome) return;
        const match = encontrarProdutoPalete(nome, produtosPalete);
        if (match) porProduto.set(match.chave, (porProduto.get(match.chave) ?? 0) + lp.qtd);
      });
      const produtos = Array.from(porProduto.keys());
      produtos.forEach((nome) => {
        const vols = getVolumes(nome);
        const numVols = vols.length;
        const cfg = getConfigProduto(nome);
        const nomeExib = cfg?.produto ?? nome;
        cores.forEach((cor) => {
          vols.forEach(({ vol, qtd }) => {
            const q = Number(item.quantidadePorCor[chaveQtd(nome, cor, vol)]) || 0;
            if (q <= 0) return;
            const totalFolhas = Math.ceil(q / qtd);
            const fracao = `${vol}/${numVols}`;
            for (let i = 0; i < totalFolhas; i++) {
              folhas.push({
                produto: nomeExib,
                cor,
                fracao,
                lote: item.lote,
              });
            }
          });
        });
      });
    });
    return folhas;
  }, [fila, lotesProdutos, cores, produtosPalete]);

  const folhasAtuais = useMemo(() => {
    const folhas: Array<{ produto: string; cor: string; fracao: string; lote: string }> = [];
    if (!loteSelecionado) return folhas;
    produtosDoLote.forEach(({ nome }) => {
      const vols = getVolumes(nome);
      const numVols = vols.length;
      const cfg = getConfigProduto(nome);
      const nomeExib = cfg?.produto ?? nome;
      cores.forEach((cor) => {
        vols.forEach(({ vol, qtd }) => {
          const q = getQtdCor(nome, cor, vol);
          if (q <= 0) return;
          const totalFolhas = Math.ceil(q / qtd);
          const fracao = `${vol}/${numVols}`;
          for (let i = 0; i < totalFolhas; i++) {
            folhas.push({
              produto: nomeExib,
              cor,
              fracao,
              lote: loteSelecionado,
            });
          }
        });
      });
    });
    return folhas;
  }, [produtosDoLote, quantidadePorCor, cores, loteSelecionado]);

  const todasFolhas = useMemo(() => {
    return folhasDeFila;
  }, [folhasDeFila]);

  const previewUnicos = useMemo(() => {
    const mapa = new Map<string, { produto: string; cor: string; fracao: string; lote: string; qtd: number }>();
    todasFolhas.forEach((f) => {
      const chave = `${f.produto}|${f.cor}|${f.fracao}|${f.lote}`;
      const existente = mapa.get(chave);
      if (existente) existente.qtd += 1;
      else mapa.set(chave, { ...f, qtd: 1 });
    });
    return Array.from(mapa.values());
  }, [todasFolhas]);

  const adicionarAFila = () => {
    if (!loteSelecionado || folhasAtuais.length === 0) return;
    const temDados = Object.keys(quantidadePorCor).some((k) => k.startsWith(produtosDoLote[0]?.nome + '::'));
    if (!temDados) return;
    setFila((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        lote: loteSelecionado,
        quantidadePorCor: { ...quantidadePorCor },
      },
    ]);
    setQuantidadePorCor({});
    setLoteSelecionado('');
  };

  const removerDaFila = (id: string) => {
    setFila((prev) => prev.filter((f) => f.id !== id));
  };

  const limparFila = () => {
    setFila([]);
  };

  const alternarLoteImpresso = (lote: string) => {
    const loteNorm = normalizar(lote);
    if (!loteNorm) return;
    setLotesImpressos((prev) => {
      if (prev.includes(loteNorm)) return prev.filter((l) => l !== loteNorm);
      return [...prev, loteNorm].sort();
    });
  };

  const limparLotesImpressos = () => {
    setLotesImpressos([]);
  };

  const imprimir = () => {
    const folhas = todasFolhas;
    if (folhas.length === 0) return;
    const conteudo = folhas
      .map(
        (f) => {
          const linha3 = f.fracao === '1/1' ? `LOTE: ${f.lote}` : `${f.fracao} LOTE: ${f.lote}`;
          const tamanhoNome = (f.produto || '').trim().length;
          const classeLinha1Longa = tamanhoNome > 20 ? 'linha1-muito-longa' : (tamanhoNome > 14 ? 'linha1-longa' : '');
          return `
      <div class="folha-palete">
        <p class="linha1 ${classeLinha1Longa}">${f.produto}</p>
        <p class="linha2">${f.cor}</p>
        <p class="linha3">${linha3}</p>
      </div>
    `;
        }
      )
      .join('');
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <html><head><title>Folhas Embalagem</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box;}
        body{font-family:Calibri,Arial,sans-serif; color:#000;}
        .folha-palete{
          page-break-after:always;
          page-break-inside:avoid;
          width:210mm;
          height:297mm;
          min-height:297mm;
          max-height:297mm;
          display:flex;
          flex-direction:column;
          justify-content:center;
          align-items:center;
          padding:20mm;
          text-align:center;
          overflow:hidden;
        }
        .folha-palete .linha1,.folha-palete .linha2,.folha-palete .linha3{
          font-size:110pt;
          font-weight:bold;
          margin:0 0 15mm 0;
          line-height:1.1;
          white-space:nowrap;
          page-break-after:avoid;
          page-break-inside:avoid;
        }
        .folha-palete .linha1.linha1-longa{
          font-size:88pt;
        }
        .folha-palete .linha1.linha1-muito-longa{
          font-size:76pt;
        }
        .folha-palete .linha3{margin-bottom:0;}
        @media print{
          body{margin:0;}
          .folha-palete{
            page-break-after:always !important;
            page-break-inside:avoid !important;
            height:297mm !important;
            min-height:297mm !important;
          }
          .folha-palete .linha1,.folha-palete .linha2,.folha-palete .linha3{
            white-space:nowrap !important;
            page-break-after:avoid !important;
            page-break-inside:avoid !important;
          }
          .folha-palete .linha1.linha1-longa{
            font-size:88pt !important;
          }
          .folha-palete .linha1.linha1-muito-longa{
            font-size:76pt !important;
          }
        }
        @media print and (orientation:landscape){
          .folha-palete{width:297mm !important; height:210mm !important; min-height:210mm !important;}
          .folha-palete .linha1,.folha-palete .linha2,.folha-palete .linha3{font-size:88pt !important;}
          .folha-palete .linha1.linha1-longa{font-size:74pt !important;}
          .folha-palete .linha1.linha1-muito-longa{font-size:64pt !important;}
        }
      </style>
      </head><body>${conteudo}</body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
      win.close();
    }, 250);
    // Marca os lotes da fila como impressos para facilitar retomar depois.
    const lotesDaFila = Array.from(new Set(fila.map((f) => normalizar(f.lote)).filter(Boolean)));
    if (lotesDaFila.length > 0) {
      setLotesImpressos((prev) => Array.from(new Set([...prev, ...lotesDaFila])).sort());
    }
    setMostrarPreview(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-black flex items-center gap-2">
          <Layers className="w-5 h-5" /> Produto por Palete
        </h3>
        <p className="text-sm text-neutral-600 mt-1">
          Lista pré-definida de produtos. Busque por lote, informe quantidade por cor e volume, adicione à fila e imprima vários lotes de uma vez.
        </p>
      </div>

      {/* 1. Lista produtos (somente leitura) */}
      <div className="bg-[#fafbfc] rounded-xl p-4 border border-[#009b3a]/15">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <h4 className="font-semibold text-black">Produtos e qtd por palete (VOL. 1, 2, 3)</h4>
          <button
            type="button"
            onClick={adicionarProdutoPalete}
            className="px-3 py-1.5 ade-btn-gold relative z-10 text-xs font-semibold rounded-lg flex items-center gap-1 shrink-0"
          >
            <Plus className="w-3.5 h-3.5" />
            Adicionar produto
          </button>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
            <input
              type="text"
              value={buscaProdutoPalete}
              onChange={(e) => setBuscaProdutoPalete(e.target.value)}
              placeholder="Buscar produto por nome..."
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm"
            />
          </div>
          {buscaProdutoPalete.trim() && (
            <button
              type="button"
              onClick={() => setBuscaProdutoPalete('')}
              className="px-3 py-2 text-sm bg-[#e4e6ec] hover:bg-[#dde0e6] text-black rounded-lg shrink-0"
            >
              Limpar busca
            </button>
          )}
        </div>
        {buscaProdutoPalete.trim() && (
          <p className="text-xs text-neutral-600 mb-2">
            {produtosPaleteVisiveis.length} de {produtosPalete.length} produto(s) encontrado(s)
          </p>
        )}
        <div className="overflow-x-auto max-h-48 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#e4e6ec]">
              <tr className="text-left text-neutral-600 border-b">
                <th className="py-2">Produto</th>
                <th className="py-2 text-center w-20">Vol. 1</th>
                <th className="py-2 text-center w-20">Vol. 2</th>
                <th className="py-2 text-center w-20">Vol. 3</th>
                <th className="py-2 text-center w-16">Ação</th>
              </tr>
            </thead>
            <tbody>
              {produtosPaleteVisiveis.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-4 text-center text-sm text-neutral-600">
                    Nenhum produto encontrado para &quot;{buscaProdutoPalete.trim()}&quot;.
                  </td>
                </tr>
              ) : (
                produtosPaleteVisiveis.map((p) => (
                <tr key={p.id} className="border-b last:border-0">
                  <td className="py-1 font-medium pr-2">
                    <input
                      type="text"
                      value={p.produto}
                      onChange={(e) => atualizarProdutoPalete(p.id, 'produto', e.target.value)}
                      onBlur={ordenarProdutosPaleteAgora}
                      className="w-full px-2 py-1 rounded border border-[#009b3a]/22 text-xs"
                      placeholder="Nome do produto"
                    />
                  </td>
                  <td className="py-1 text-center">
                    <input
                      type="number"
                      value={p.vol1}
                      onChange={(e) => atualizarProdutoPalete(p.id, 'vol1', e.target.value)}
                      className="w-16 px-1 py-1 rounded border border-[#009b3a]/22 text-xs text-center"
                      min={0}
                    />
                  </td>
                  <td className="py-1 text-center">
                    <input
                      type="number"
                      value={p.vol2 ?? ''}
                      onChange={(e) => atualizarProdutoPalete(p.id, 'vol2', e.target.value)}
                      className="w-16 px-1 py-1 rounded border border-[#009b3a]/22 text-xs text-center"
                      min={0}
                    />
                  </td>
                  <td className="py-1 text-center">
                    <input
                      type="number"
                      value={p.vol3 ?? ''}
                      onChange={(e) => atualizarProdutoPalete(p.id, 'vol3', e.target.value)}
                      className="w-16 px-1 py-1 rounded border border-[#009b3a]/22 text-xs text-center"
                      min={0}
                    />
                  </td>
                  <td className="py-1 text-center">
                    <button
                      type="button"
                      onClick={() => removerProdutoPalete(p.id)}
                      className="text-rose-500 hover:text-rose-700"
                      title="Remover produto"
                    >
                      <Trash2 className="w-4 h-4 inline-block" />
                    </button>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 2. Cores */}
      <div className="bg-[#fafbfc] rounded-xl p-4 border border-[#009b3a]/15">
        <h4 className="font-semibold text-black mb-3">Cores disponíveis</h4>
        <div className="flex gap-2 mb-3 flex-wrap">
          <input
            type="text"
            value={novaCor}
            onChange={(e) => setNovaCor(e.target.value.toUpperCase())}
            placeholder="Nova cor..."
            className="px-3 py-2 rounded-lg border border-[#009b3a]/22 w-40"
            onKeyDown={(e) => e.key === 'Enter' && adicionarCor()}
          />
          <button type="button" onClick={adicionarCor} className="ade-neu-card-sm rounded-lg px-4 py-2 font-semibold text-black hover:brightness-105">
            Adicionar cor
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {cores.map((c) => (
            <span key={c} className="px-3 py-1 bg-[#e4e6ec] rounded-full text-sm text-black">
              {c}
            </span>
          ))}
        </div>
      </div>

      {/* 3. Busca por lote e entrada de qtd por cor/volume */}
      <div className="bg-[#fafbfc] rounded-xl p-4 border border-[#009b3a]/15">
        <h4 className="font-semibold text-black mb-3 flex items-center gap-2">
          <Search className="w-4 h-4" /> Buscar por lote
        </h4>
        <div className="flex gap-3 flex-wrap items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-neutral-600 mb-1">Lote</label>
            <p className="mb-1 text-xs font-semibold text-[#009b3a]">
              Lote que parei: {ultimoLoteParado || '-'}
            </p>
            <select
              value={loteSelecionado}
              onChange={(e) => {
                const lote = e.target.value;
                setLoteSelecionado(lote);
                setUltimoLoteParado(normalizar(lote));
              }}
              className="w-full px-3 py-2 rounded-lg border border-[#009b3a]/22"
            >
              <option value="">Selecione o lote...</option>
              {lotesUnicos.map((l) => (
                <option key={l} value={l}>
                  {lotesImpressos.includes(normalizar(l)) ? `✓ ${l}` : l}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => alternarLoteImpresso(loteSelecionado)}
            disabled={!loteSelecionado}
            className="ade-neu-card-sm rounded-lg px-4 py-2 font-semibold text-black hover:brightness-105 disabled:opacity-40"
          >
            {loteSelecionadoImpresso ? 'Desmarcar impresso' : 'Marcar como impresso'}
          </button>
          <button
            onClick={adicionarAFila}
            disabled={!loteSelecionado || folhasAtuais.length === 0}
            className="ade-btn-gold relative z-10 rounded-lg px-4 py-2 disabled:opacity-40"
          >
            <Plus className="w-4 h-4 inline mr-1" /> Adicionar à fila
          </button>
        </div>
        {lotesImpressos.length > 0 && (
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-neutral-600">
              Lotes já impressos: <span className="font-semibold text-black">{lotesImpressos.length}</span>
            </p>
            <button
              type="button"
              onClick={limparLotesImpressos}
              className="text-xs px-3 py-1.5 bg-[#e4e6ec] hover:bg-[#dde0e6] text-black rounded-md"
            >
              Limpar marcações
            </button>
          </div>
        )}

        {produtosDoLote.length > 0 && (
          <div className="mt-4 overflow-x-auto rounded-lg border border-[#009b3a]/15">
            <table className="w-full min-w-max border-separate border-spacing-0 text-sm">
              <thead>
                <tr className="text-left text-neutral-600 border-b">
                  <th className="sticky left-0 z-[21] w-[12rem] shrink-0 bg-[#e4e6ec] py-2 align-middle shadow-[4px_0_8px_-4px_rgba(0,0,0,0.12)] border-b border-r border-[#009b3a]/20">
                    Produto
                  </th>
                  <th className="sticky left-[12rem] z-[20] w-[4.5rem] shrink-0 bg-[#e4e6ec] py-2 align-middle shadow-[4px_0_8px_-4px_rgba(0,0,0,0.12)] border-b border-r border-[#009b3a]/20">
                    Qtd total
                  </th>
                  {cores.map((cor) => (
                    <th key={cor} colSpan={3} className="py-2 px-1 text-center border-l border-[#009b3a]/15 bg-[#e4e6ec]">
                      {cor}
                    </th>
                  ))}
                </tr>
                <tr className="text-left text-neutral-600 border-b text-xs">
                  <th className="sticky left-0 z-[21] w-[12rem] shrink-0 bg-[#e4e6ec] py-1 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.12)] border-r border-[#009b3a]/20" />
                  <th className="sticky left-[12rem] z-[20] w-[4.5rem] shrink-0 bg-[#e4e6ec] py-1 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.12)] border-r border-[#009b3a]/20" />
                  {cores.flatMap((cor) => [
                    <th key={`${cor}-v1`} className="bg-[#e4e6ec] py-1 px-1 text-center border-l border-[#009b3a]/15">V1</th>,
                    <th key={`${cor}-v2`} className="bg-[#e4e6ec] py-1 px-1 text-center">V2</th>,
                    <th key={`${cor}-v3`} className="bg-[#e4e6ec] py-1 px-1 text-center">V3</th>,
                  ])}
                </tr>
              </thead>
              <tbody>
                {produtosDoLote.map(({ nome, qtd }) => {
                  const cfg = getConfigProduto(nome);
                  const vols = getVolumes(nome);
                  return (
                    <tr key={nome} className="border-b last:border-0">
                      <td
                        className="sticky left-0 z-[11] w-[12rem] shrink-0 truncate bg-[#fafbfc] py-2 align-middle font-medium shadow-[4px_0_8px_-4px_rgba(0,0,0,0.1)] border-r border-[#009b3a]/15"
                        title={(cfg || {}).produto || nome}
                      >
                        {(cfg || {}).produto || nome}
                      </td>
                      <td className="sticky left-[12rem] z-[10] w-[4.5rem] shrink-0 bg-[#fafbfc] py-2 align-middle text-center shadow-[4px_0_8px_-4px_rgba(0,0,0,0.1)] border-r border-[#009b3a]/15">
                        {qtd}
                      </td>
                      {cores.flatMap((cor) =>
                        [1, 2, 3].map((v) => (
                          <td key={`${nome}-${cor}-${v}`} className="py-1 px-1 border-l">
                            {vols.some((x) => x.vol === v) ? (
                              <input
                                type="number"
                                value={getQtdCor(nome, cor, v) || ''}
                                onChange={(e) => atualizarQtdCor(nome, cor, v, Number(e.target.value) || 0)}
                                min={0}
                                className="w-14 px-1 py-0.5 text-center rounded border border-[#009b3a]/22 text-xs"
                              />
                            ) : (
                              <span className="text-neutral-600">-</span>
                            )}
                          </td>
                        ))
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 4. Fila e preview */}
      <div className="bg-[#fafbfc] rounded-xl p-4 border border-[#009b3a]/15">
        <h4 className="font-semibold text-black mb-3">Fila de impressão ({todasFolhas.length} folhas A4)</h4>
        {fila.length > 0 && (
          <>
            <div className="space-y-2 mb-3">
              {fila.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-2 bg-[#eef0f4] rounded-lg">
                  <span className="font-medium">
                    Lote: {item.lote}
                    {lotesImpressos.includes(normalizar(item.lote)) && (
                      <span className="ml-2 rounded-full bg-[#e8f5ec] px-2 py-0.5 text-xs text-[#009b3a]">Impresso</span>
                    )}
                  </span>
                  <button onClick={() => removerDaFila(item.id)} className="text-rose-500 hover:text-rose-700">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => setMostrarPreview(true)}
                className="px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 font-semibold rounded-lg flex items-center gap-2"
              >
                <Eye className="w-4 h-4" /> Visualizar antes de imprimir
              </button>
              <button
                onClick={imprimir}
                className="ade-btn-gold relative z-10 flex items-center gap-2 rounded-lg px-6 py-2"
              >
                <Printer className="w-4 h-4" /> Imprimir
              </button>
              <button type="button" onClick={limparFila} className="ade-neu-card-sm rounded-lg px-4 py-2 font-semibold text-black hover:brightness-105">
                Limpar fila
              </button>
            </div>
          </>
        )}
        {fila.length === 0 && (
          <p className="text-neutral-600 text-sm">Selecione um lote, preencha as quantidades por cor/volume e clique em &quot;Adicionar à fila&quot;. Depois visualize e imprima.</p>
        )}
      </div>

      {/* Modal Preview - apenas combinações únicas (informações diferentes) */}
      {mostrarPreview && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#fafbfc] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h4 className="font-bold text-black">Pré-visualização — {todasFolhas.length} folha(s) A4 (agrupadas por tipo)</h4>
              <div className="flex gap-2">
                <button onClick={() => setMostrarPreview(false)} className="px-4 py-2 bg-[#dde0e6] rounded-lg">Fechar</button>
                <button type="button" onClick={imprimir} className="ade-btn-gold relative z-10 flex items-center gap-2 rounded-lg px-4 py-2">
                  <Printer className="w-4 h-4" /> Imprimir
                </button>
              </div>
            </div>
            <div className="overflow-auto p-4">
              <p className="text-sm text-neutral-600 mb-3">Resumo — cada linha abaixo representa um tipo de folha que será impressa. A impressão gerará {todasFolhas.length} folha(s) A4 no total.</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-neutral-600 border-b">
                    <th className="py-2">Produto</th>
                    <th className="py-2">Cor</th>
                    <th className="py-2">Volume</th>
                    <th className="py-2">Lote</th>
                    <th className="py-2 text-right">Qtd. folhas</th>
                  </tr>
                </thead>
                <tbody>
                  {previewUnicos.map((f, i) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-[#eef0f4]">
                      <td className="py-2 font-medium">{f.produto}</td>
                      <td className="py-2">{f.cor}</td>
                      <td className="py-2">{f.fracao === '1/1' ? '—' : f.fracao}</td>
                      <td className="py-2">{f.lote}</td>
                      <td className="py-2 text-right font-semibold">{f.qtd}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
